import { RecaptchaVerifier, multiFactor, PhoneAuthProvider, PhoneMultiFactorGenerator } from 'firebase/auth';
import { getMfaSmsErrorMessage } from '../utils/authErrorMessages.js';

/**
 * Firebase-supported phone auth regions
 * @see https://firebase.google.com/support/faq#phone-auth-regions
 * These are countries with confirmed good SMS delivery rates
 */
const FIREBASE_SUPPORTED_COUNTRIES = [
    'AD',
    'AE',
    'AF',
    'AG',
    'AL',
    'AM',
    'AO',
    'AR',
    'AS',
    'AT',
    'AU',
    'AW',
    'AZ',
    'BA',
    'BB',
    'BD',
    'BE',
    'BF',
    'BG',
    'BJ',
    'BM',
    'BN',
    'BO',
    'BR',
    'BS',
    'BT',
    'BW',
    'BY',
    'BZ',
    'CA',
    'CD',
    'CF',
    'CG',
    'CH',
    'CI',
    'CK',
    'CL',
    'CM',
    'CO',
    'CR',
    'CV',
    'CW',
    'CY',
    'CZ',
    'DE',
    'DJ',
    'DK',
    'DM',
    'DO',
    'DZ',
    'EC',
    'EG',
    'ES',
    'ET',
    'FI',
    'FJ',
    'FK',
    'FM',
    'FO',
    'FR',
    'GA',
    'GB',
    'GD',
    'GE',
    'GF',
    'GG',
    'GH',
    'GI',
    'GL',
    'GM',
    'GP',
    'GQ',
    'GR',
    'GT',
    'GY',
    'HK',
    'HN',
    'HR',
    'HT',
    'HU',
    'ID',
    'IE',
    'IL',
    'IM',
    'IN',
    'IQ',
    'IT',
    'JE',
    'JM',
    'JO',
    'JP',
    'KE',
    'KG',
    'KH',
    'KM',
    'KN',
    'KR',
    'KW',
    'KY',
    'KZ',
    'LA',
    'LB',
    'LC',
    'LI',
    'LK',
    'LS',
    'LT',
    'LU',
    'LV',
    'LY',
    'MA',
    'MD',
    'ME',
    'MF',
    'MG',
    'MK',
    'MM',
    'MN',
    'MO',
    'MS',
    'MT',
    'MU',
    'MW',
    'MX',
    'MY',
    'MZ',
    'NA',
    'NC',
    'NE',
    'NF',
    'NG',
    'NI',
    'NL',
    'NO',
    'NP',
    'NZ',
    'OM',
    'PA',
    'PE',
    'PG',
    'PH',
    'PK',
    'PL',
    'PM',
    'PR',
    'PS',
    'PT',
    'PY',
    'QA',
    'RE',
    'RO',
    'RS',
    'RU',
    'RW',
    'SA',
    'SC',
    'SE',
    'SG',
    'SH',
    'SI',
    'SK',
    'SL',
    'SN',
    'SR',
    'ST',
    'SV',
    'SZ',
    'TC',
    'TG',
    'TH',
    'TL',
    'TM',
    'TO',
    'TR',
    'TT',
    'TW',
    'TZ',
    'UA',
    'UG',
    'US',
    'UY',
    'UZ',
    'VC',
    'VE',
    'VG',
    'VI',
    'VN',
    'WS',
    'YE',
    'YT',
    'ZA',
    'ZM',
    'ZW',
];
/**
 * MFA SMS error class
 * Extends Error to satisfy linter requirements for throwing error objects
 */
class MfaSmsError extends Error {
    constructor(code, message) {
        super(message);
        this.name = 'MfaSmsError';
        this.code = code;
        // Maintains proper stack trace for where our error was thrown (only available on V8)
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, MfaSmsError);
        }
    }
}
/**
 * Service for handling SMS MFA enrollment with Firebase
 */
class MfaSmsService {
    constructor(auth) {
        this.auth = auth;
    }
    /**
     * Creates and returns a RecaptchaVerifier instance
     * Uses invisible reCAPTCHA for better UX
     */
    createRecaptchaVerifier(containerId) {
        // Disable app verification for testing in non-production environments
        if (import.meta.env.VITE_CURR_ENV === 'local' ||
            import.meta.env.VITE_CURR_ENV === 'development' ||
            import.meta.env.VITE_CURR_ENV === 'staging') {
            this.auth.settings.appVerificationDisabledForTesting = true;
        }
        return new RecaptchaVerifier(this.auth, containerId, {
            size: 'invisible',
        });
    }
    /**
     * Sends SMS verification code to the provided phone number
     * @param user - Current authenticated user
     * @param phoneNumber - Phone number in E.164 format (e.g., +1234567890)
     * @param recaptchaVerifier - RecaptchaVerifier instance
     * @returns Promise resolving to verification ID
     */
    async sendVerificationCode(user, phoneNumber, recaptchaVerifier) {
        try {
            // Get multi-factor session for enrollment
            const multiFactorSession = await multiFactor(user).getSession();
            // Create phone auth provider
            const phoneAuthProvider = new PhoneAuthProvider(this.auth);
            // Prepare phone verification options
            const phoneInfoOptions = {
                phoneNumber,
                session: multiFactorSession,
            };
            // Send SMS verification code
            const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);
            return verificationId;
        }
        catch (error) {
            const firebaseError = error;
            throw new MfaSmsError(firebaseError.code || 'mfa/unknown-error', getMfaSmsErrorMessage(firebaseError.code) ||
                firebaseError.message ||
                'Failed to send verification code');
        }
    }
    /**
     * Verifies the SMS code and enrolls the phone number as an MFA factor
     * @param user - Current authenticated user
     * @param verificationId - Verification ID from sendVerificationCode
     * @param verificationCode - 6-digit SMS verification code
     * @returns Promise that resolves when enrollment is complete
     */
    async verifyAndEnroll(user, verificationId, verificationCode) {
        try {
            // Create phone auth credential
            const phoneAuthCredential = PhoneAuthProvider.credential(verificationId, verificationCode);
            // Create multi-factor assertion
            const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(phoneAuthCredential);
            // Enroll the phone number as an MFA factor
            await multiFactor(user).enroll(multiFactorAssertion, 'user-phone-number');
        }
        catch (error) {
            const firebaseError = error;
            throw new MfaSmsError(firebaseError.code || 'mfa/unknown-error', getMfaSmsErrorMessage(firebaseError.code) ||
                firebaseError.message ||
                'Failed to verify code and enroll');
        }
    }
    /**
     * Formats phone number to E.164 format
     * Assumes US phone numbers if country code is missing
     * @param phoneNumber - Phone number in any format
     * @returns Formatted phone number in E.164 format
     */
    formatPhoneNumber(phoneNumber) {
        // Remove all non-digit characters
        const digits = phoneNumber.replace(/\D/g, '');
        // If already starts with country code (11+ digits starting with 1), return with +
        if (digits.length >= 11 && digits.startsWith('1')) {
            return `+${digits}`;
        }
        // If 10 digits, assume US number and add +1
        if (digits.length === 10) {
            return `+1${digits}`;
        }
        // If less than 10 digits, return as is (validation will catch it)
        return digits.length > 0 ? `+${digits}` : phoneNumber;
    }
    /**
     * Validates phone number format
     * @param phoneNumber - Phone number to validate
     * @returns true if valid, false otherwise
     */
    validatePhoneNumber(phoneNumber) {
        const formatted = this.formatPhoneNumber(phoneNumber);
        // E.164 format: + followed by 1-15 digits
        const e164Regex = /^\+[1-9]\d{1,14}$/;
        return e164Regex.test(formatted);
    }
}

export { FIREBASE_SUPPORTED_COUNTRIES, MfaSmsError, MfaSmsService };
//# sourceMappingURL=mfaSmsService.js.map
